/**
 * Drag-to-reorder for a grid of tappable items (wallet cards, category tiles).
 *
 * - Mouse: press and move a few pixels and the item lifts at once (no waiting).
 * - Touch/pen: a short press (~⅓ s) lifts it, then drag. Moving before that counts as a
 *   scroll, so swiping across the grid still scrolls the page.
 * - A quick tap/click stays a normal tap; the click after a drag is swallowed.
 * - Esc or an interrupted touch puts everything back. Release saves via onCommit.
 * Pointer Events cover mouse, touch and pen with one code path (no drag library).
 */
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { moveTo } from './entityOrder.ts';

/** Mouse travel (px) that turns a press into a drag. */
const MOUSE_START_PX = 6;
/** Finger travel (px) during the short press that means "scroll", not "drag". */
const TOUCH_SLOP_PX = 10;

export interface DragState {
  id: string;
  /** Where in the item it was picked up. */
  offX: number;
  offY: number;
  /** Current pointer position. */
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Press {
  id: string;
  x: number;
  y: number;
  pointerId: number;
  mouse: boolean;
}

export function useHoldDrag({
  ids,
  onCommit,
  touchHoldMs = 350,
}: {
  ids: string[];
  onCommit: (ids: string[]) => void;
  touchHoldMs?: number;
}) {
  const [pressed, setPressed] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [preview, setPreview] = useState<string[] | null>(null);
  const els = useRef(new Map<string, HTMLElement>());
  const press = useRef<Press | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const suppressClick = useRef(false);
  // Latest values for the window listeners, which are bound once per gesture.
  const live = useRef({ drag, preview, ids, onCommit });
  live.current = { drag, preview, ids, onCommit };

  function end() {
    window.clearTimeout(timer.current);
    press.current = null;
    setPressed(null);
    setDrag(null);
    setPreview(null);
  }

  function lift(p: Press, x: number, y: number) {
    const el = els.current.get(p.id);
    if (!el) return;
    const r = el.getBoundingClientRect();
    suppressClick.current = true; // letting go must not also tap the item
    navigator.vibrate?.(12);
    setPressed(null);
    setPreview(live.current.ids);
    setDrag({ id: p.id, offX: p.x - r.left, offY: p.y - r.top, x, y, w: r.width, h: r.height });
  }

  function onPointerDown(e: ReactPointerEvent, id: string) {
    if (e.button !== 0) return; // primary mouse button, touch or pen
    suppressClick.current = false;
    const p: Press = { id, x: e.clientX, y: e.clientY, pointerId: e.pointerId, mouse: e.pointerType === 'mouse' };
    press.current = p;
    setPressed(id);
    window.clearTimeout(timer.current);
    if (!p.mouse) timer.current = window.setTimeout(() => press.current && lift(press.current, p.x, p.y), touchHoldMs);
  }

  const active = pressed !== null || drag !== null;
  useEffect(() => {
    if (!active) return;
    function onMove(e: PointerEvent) {
      const p = press.current;
      if (!p || e.pointerId !== p.pointerId) return;
      const d = live.current.drag;
      if (!d) {
        const dist = Math.hypot(e.clientX - p.x, e.clientY - p.y);
        if (p.mouse && dist > MOUSE_START_PX) lift(p, e.clientX, e.clientY);
        else if (!p.mouse && dist > TOUCH_SLOP_PX) end(); // a scroll, not a drag
        return;
      }
      setDrag({ ...d, x: e.clientX, y: e.clientY });
      for (const [id, el] of els.current) {
        if (id === d.id) continue;
        const r = el.getBoundingClientRect();
        if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
          const current = live.current.preview ?? live.current.ids;
          const next = moveTo(current, d.id, current.indexOf(id));
          if (next !== current) setPreview(next);
          break;
        }
      }
    }
    function onUp(e: PointerEvent) {
      const p = press.current;
      if (p && e.pointerId !== p.pointerId) return;
      const { drag: d, preview: finalOrder, ids: before, onCommit: commit } = live.current;
      if (d && finalOrder && finalOrder.join() !== before.join()) {
        commit(finalOrder);
        navigator.vibrate?.(8);
      }
      end();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') end();
    }
    // Keep the page from scrolling under the finger while an item is lifted.
    function onTouchMove(e: TouchEvent) {
      if (live.current.drag && e.cancelable) e.preventDefault();
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', end); // interrupted: put everything back
    window.addEventListener('keydown', onKey);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', end);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [active]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  /** Spread onto each item: <button {...bind(id)} onClick={…}>. */
  function bind(id: string) {
    return {
      ref: (el: HTMLElement | null) => {
        if (el) els.current.set(id, el);
        else els.current.delete(id);
      },
      onPointerDown: (e: ReactPointerEvent) => onPointerDown(e, id),
      onClickCapture: (e: ReactMouseEvent) => {
        if (suppressClick.current) {
          e.preventDefault();
          e.stopPropagation();
          suppressClick.current = false;
        }
      },
      onContextMenu: (e: ReactMouseEvent) => e.preventDefault(), // no long-press menu on phones
    };
  }

  return {
    /** Items in display order (live preview while dragging). */
    order: preview ?? ids,
    drag,
    /** Item currently pressed but not yet lifted (for press feedback). */
    pressed,
    isSlot: (id: string) => drag?.id === id,
    bind,
  };
}
